import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useHall } from '@/contexts/HallProvider'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { useAxios } from '@/contexts/AxiosProvider'
import { CheckIcon, ClipboardIcon, TimerReset, TriangleAlertIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { VoiceSelector } from '../voiceSelector'
import { BotUserId2 } from '@/constants'
import { WinConditionFormField } from '../settings/WinCondition'
import { useAzureTts } from '@/contexts/AzureTtsProvider'
import { useMeeting } from '@videosdk.live/react-sdk'
import { useMisc } from '@/contexts/MiscProvider'
import { DebateFormatFormField } from '../settings/DebateFormatFormField'
import { useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useRouter } from '@/hooks/useRouter'

/* ------------------------------ Schema ------------------------------ */

const formSchema = z.object({
  title: z.string().min(1, 'Title is required.'),
  topic: z.string().min(1, 'Topic is required.'),
  isPublic: z.boolean(),
  format: z.string().min(1, 'Format is required.'),
  affirmingTeamName: z.string(),
  dissentingTeamName: z.string(),
  evaluationCriteria: z.array(z.string()).max(5, 'Select up to 5 criteria').optional(),
  winType: z.string(),
  globalAI: z.number(),
})

/* --------------------------- Small components --------------------------- */

const MeetingIdCopyBTN = ({ hall, isOwner }: { hall: any; isOwner: boolean }) => {
  const [isCopied, setIsCopied] = useState(false)

  const copyToClipboard = () => {
    const textToCopy = `${window.location.href}?inviteCode=${hall.inviteCode}`
    navigator.clipboard.writeText(textToCopy)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 3000)
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Button variant="outline" onClick={copyToClipboard} className="max-w-[225px]">
            <h1 className="light:text-black text-base font-semibold truncate">
              {hall?.inviteCode}
            </h1>
            <div className="ml-2">
              {isCopied ? (
                <CheckIcon className="h-5 w-5 text-green-400" />
              ) : (
                <ClipboardIcon className="h-5 w-5 text-black dark:text-white" />
              )}
            </div>
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>Copy Link With Invite Code</p>
      </TooltipContent>
    </Tooltip>
  )
}

/* --------------------------- iOS scroll guard --------------------------- */
/** Prevents the iOS “edge lock” where scrolling gets stuck at the top/bottom
 *  of a scrollable div until the user rotates the device. */
function useIOSScrollFix(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Make sure the layer never sits exactly at 0 or max on first touch
    const onTouchStart = () => {
      if (el.scrollTop <= 0) el.scrollTop = 1
      const max = el.scrollHeight - el.clientHeight
      if (el.scrollTop >= max) el.scrollTop = max - 1
    }

    // During scroll, keep it away from hard edges (prevents lock)
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight
      if (el.scrollTop <= 0) el.scrollTop = 1
      else if (el.scrollTop >= max) el.scrollTop = max - 1
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('scroll', onScroll, { passive: true })

    // Seed once on mount
    if (el.scrollTop === 0) el.scrollTop = 1

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('scroll', onScroll)
    }
  }, [ref])
}

/* ------------------------------ Main panel ------------------------------ */

export const SettingsPanel: React.FC<{ panelHeight?: number }> = ({ panelHeight }) => {
  const { hall, updateHall, hallMetadata, setHallMetadata, setLocalMetadata, isOwner, setLoaderMessages } = useHall()
  const [isEditing, setIsEditing] = useState(false)
  const { hallUsers } = useHallUsers()
  const { resetFlowNotes } = useFlowNotes()
  const router = useRouter()

  const axios = useAxios()
  const { pauseReading } = useAzureTts()
  const { leave } = useMeeting()
  const { reset } = useMisc()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...hall,
      globalAI: hall.globalAI?.id,
    },
  })

  const onSubmit = async (values: any) => {
    try {
      updateHall(values)
      if (hall.format !== values.format) resetFlowNotes(values.format)
      setIsEditing(false)
    } catch (error) {
      // no-op
    }
  }

  const handleDeleteHall = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    try {
      pauseReading()
      await axios.delete(`/api/hall/${hall.id}`)
      // Mark closed locally without publishing to the server to avoid 404s after deletion
      setLocalMetadata({ isClosed: true })
      try {
        leave()
      } catch {}
    } finally {
      // Always route back to home to avoid rendering a blank meeting screen after leaving.
      router.push('/')
    }
  }

  const handleResetHall = async () => {
    setLoaderMessages(['Resetting your hall'])
    const { data } = await axios.post(`/api/hall/${hall.hallId}/reset`)
    reset(data)
    setLoaderMessages([])
  }

  const showInviteBtn = useMemo(() => {
    if (hall?.isPublic) return true
    if (!hall?.isPublic && isOwner) return true
    return false
  }, [hall, isOwner])

  const hasBot2 = useMemo(() => hallUsers.some((user) => user.id === BotUserId2), [hallUsers])

  // iOS scroll target
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useIOSScrollFix(scrollRef)

  return (
    <div className="h-full flex flex-col min-h-0 rounded-md w-full dark:text-white">
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 p-2 pb-16 overflow-y-auto noScrollbar ios-scroll"
        style={{
          height: panelHeight ? `${panelHeight}px` : undefined,
          WebkitOverflowScrolling: 'touch' as any,
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
        }}
      >
        {!isEditing ? (
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-bold">Title:</span> {hall.title}
            </p>
            <p>
              <span className="font-bold">Topic:</span> {hall.topic}
            </p>
            <p>
              <span className="font-bold">Public:</span> {hall.isPublic ? 'Yes' : 'No'}
            </p>
            <p>
              <span className="font-bold">Invite Code:</span> {hall.inviteCode}
            </p>
            <p>
              <span className="font-bold">Format:</span> {hall.format}
            </p>
            <p>
              <span className="font-bold">Affirming Team Name:</span> {hall.affirmingTeamName}
            </p>
            <p>
              <span className="font-bold">Dissenting Team Name:</span> {hall.dissentingTeamName}
            </p>
            {showInviteBtn && (
              <div className="flex items-center space-x-4">
                <p className="font-bold">Invite Link:</p>
                <MeetingIdCopyBTN isOwner={isOwner} hall={hall} />
              </div>
            )}

            <p>
              <span className="font-bold">Debate Bot Voice 1:</span> {hall.globalAI.name}
            </p>

            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={() => setIsEditing(true)}
                    size="sm"
                    disabled={hallMetadata?.debateInitialized}
                  >
                    Edit
                  </Button>
                </span>
              </TooltipTrigger>
              {hallMetadata?.debateInitialized && (
                <TooltipContent side="bottom" className="max-w-[300px] text-center">
                  Settings can’t be changed once the debate begins. To modify settings, the debate
                  must be reset.
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, (err) => {
                // no-op
              })}
              className="space-y-3"
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Title</FormLabel>
                    <FormControl>
                      <Input {...field} className="text-sm" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="topic"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Topic</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="text-sm" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="affirmingTeamName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Affirming Team Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="text-sm"
                        maxLength={20}
                        value={field.value.toString()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dissentingTeamName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Dissenting Team Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="text-sm"
                        maxLength={20}
                        value={field.value.toString()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="format"
                render={({ field }) => <DebateFormatFormField field={field} form={form} />}
              />

              <div className="flex w-full justify-between space-x-4">
                <FormField
                  control={form.control}
                  name="globalAI"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-sm">Debate Bot Voice 1</FormLabel>
                      <VoiceSelector
                        onValueChange={(val: string) => field.onChange(Number(val))}
                        defaultValue={field.value}
                        disabled={false}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bot1"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-sm">Bot 1 Voice</FormLabel>
                      <VoiceSelector
                        onValueChange={(val: string) => field.onChange(Number(val))}
                        defaultValue={field.value}
                        disabled={false}
                      />
                    </FormItem>
                  )}
                />
              </div>

              {hasBot2 && (
                <FormField
                  control={form.control}
                  name="bot2"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-sm">Bot 2 Voice</FormLabel>
                      <VoiceSelector
                        onValueChange={(val: string) => field.onChange(Number(val))}
                        defaultValue={field.value}
                        disabled={false}
                      />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="winType"
                render={({ field }) => <WinConditionFormField field={field} form={form} />}
              />

              <FormField
                control={form.control}
                name="isPublic"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-end space-x-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="text-sm">Make Public</FormLabel>
                  </FormItem>
                )}
              />

              <div className="flex space-x-3">
                <Button type="submit" className="text-sm" size="sm">
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="text-sm"
                  onClick={() => setIsEditing(false)}
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        )}

        {!isEditing && (
          <div className="flex w-full flex-col mt-4">
            <div className="flex w-full space-x-2 justify-between">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" onClick={handleResetHall}>
                    Reset
                    <TimerReset className="ml-2" size="20px" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px] text-center">
                  This will reset the current debate state
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="destructive" onClick={handleDeleteHall}>
                    Close Debate Hall <TriangleAlertIcon className="ml-2" size="20px" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px] text-center">
                  This will conclude the debate and remove all active users, this cannot be undone
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
